import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Instagram } from "lucide-react";
import { Eyebrow, MaskLines } from "./Reveal";
import { CONTACT, useLang } from "@/lib/i18n";
import { api, fileUrl } from "@/lib/api";

const PHOTOS = [
  { src: "/img/actros-night.jpg", pos: "50% 50%" },
  { src: "/img/iveco-day.jpg", pos: "40% 72%" },
  { src: "/img/iveco-night.jpg", pos: "45% 50%" },
];
const OFFSETS = ["lg:mt-0", "lg:mt-24", "lg:mt-10"];

const Frame = ({ src, pos, cap, i, progress }) => {
  const d = (i % 3) + 1;
  const y = useTransform(progress, [0, 1], [30 * d, -30 * d]);
  return (
    <motion.figure
      data-testid={`gallery-item-${i}`}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.1 }}
      className={`group ${OFFSETS[i % 3]}`}
    >
      <motion.div
        variants={{ hidden: { clipPath: "inset(100% 0% 0% 0% round 24px)" }, show: { clipPath: "inset(0% 0% 0% 0% round 24px)" } }}
        transition={{ duration: 1.2, delay: (i % 3) * 0.12, ease: [0.16, 1, 0.3, 1] }}
        className="relative aspect-[4/3] overflow-hidden sm:aspect-[4/5] rounded-[24px] bg-[#14171E]"
      >
        <motion.img style={{ y, scale: 1.18, objectPosition: pos }} src={src} alt={cap} loading="lazy" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-80 transition-opacity duration-500 group-hover:opacity-50" />
        <span className="absolute left-5 top-5 rounded-full border border-white/20 bg-black/40 px-3 py-1 font-mono-tc text-xs backdrop-blur-md">{String(i + 1).padStart(2, "0")}</span>
        <figcaption className="absolute inset-x-5 bottom-5 font-display text-2xl font-bold uppercase italic leading-tight">{cap}</figcaption>
      </motion.div>
    </motion.figure>
  );
};

export default function Gallery() {
  const { t, lang } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const [uploaded, setUploaded] = useState([]);
  useEffect(() => {
    if (!process.env.REACT_APP_BACKEND_URL) return;
    api.get("/photos").then((r) => setUploaded(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);
  const photos = [
    ...uploaded.map((p) => ({ src: fileUrl(p.url), pos: "50% 50%", cap: (lang === "en" && p.caption_en) || p.caption_pl || p.caption_en || "" })),
    ...PHOTOS.map((p, i) => ({ ...p, cap: t.fleet.caps[i] })),
  ];
  return (
    <section id="fleet" ref={ref} data-testid="gallery-section" className="mx-auto max-w-[1400px] px-5 py-20 sm:px-8 sm:py-28">
      <div className="mb-14 flex flex-col justify-between gap-8 md:flex-row md:items-end">
        <div>
          <Eyebrow>{t.fleet.eyebrow}</Eyebrow>
          <h2 className="font-display text-[2.5rem] font-black uppercase italic leading-[0.9] sm:text-7xl">
            <MaskLines key={lang} lines={t.fleet.title} onView />
          </h2>
        </div>
        <a data-testid="gallery-instagram-link" href={CONTACT.ig} target="_blank" rel="noreferrer" className="group flex items-center gap-3 self-start rounded-full border border-white/20 px-5 py-3 transition-colors hover:border-white hover:text-white md:self-auto">
          <Instagram className="h-5 w-5" /> {t.fleet.ig} <span className="font-mono-tc text-xs text-zinc-400 group-hover:text-white">{CONTACT.igHandle}</span>
        </a>
      </div>
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:pb-24">
        {photos.map((p, i) => (
          <Frame key={p.src} src={p.src} pos={p.pos} cap={p.cap} i={i} progress={scrollYProgress} />
        ))}
      </div>
    </section>
  );
}
